import { Box, Switch, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Label from 'src/components/label';
import BannerPreviewCell from './banner-preview-cell';
import Iconify from 'src/components/iconify';

type GetHomeBannersColumnsParams = {
  handleChangeStatus?: (benefitId: string, newStatus: 'active' | 'inactive') => void;
};

const isEmptyDateValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return true;
  }

  const normalizedValue = String(value).trim().toLowerCase();

  return normalizedValue === '' || normalizedValue === 'null' || normalizedValue === 'undefined';
};

const renderDateCell = (value: unknown, formatDate: (isoDate: string) => string) => {
  if (isEmptyDateValue(value) || Number.isNaN(new Date(String(value)).getTime())) {
    return (
      <Label
        variant="soft"
        color="default"
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          color: 'text.secondary',
          fontWeight: 700,
        }}
      >
        Sem data
      </Label>
    );
  }

  return (
    <Box sx={{ width: 120, display: 'flex', justifyContent: 'flex-start' }}>
      <Label
        variant="outlined"
        sx={{
          gap: 0.75,
          px: 1.4,
          py: 0.45,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          fontWeight: 700,
          flex: 1,
        }}
      >
        <Iconify icon="mdi:calendar-month-outline" sx={{ width: 16, height: 16 }} />
        {formatDate(String(value)) || '-'}
      </Label>
    </Box>
  );
};

function translateTier(tier?: string) {
  switch (tier?.toLowerCase()) {
    case 'all':
      return 'Todos';
    case 'silver':
      return 'prata';
    case 'gold':
      return 'ouro';
    case 'diamond':
      return 'diamante';
    default:
      return tier || 'Sem Tier';
  }
}

const tierStyles: Record<string, any> = {
  todos: {
    background: 'linear-gradient(180deg, #6f7680 0%, #4e5660 100%)',
    color: '#ffffff',
    boxShadow:
      '0 2px 6px rgba(33, 40, 48, 0.45), inset 0 1px 1px rgba(255, 255, 255, 0.18), inset 0 -1px 1px rgba(0, 0, 0, 0.18)',
    textShadow: '0 1px 1px rgba(0, 0, 0, 0.35)',
  },
  prata: {
    background:
      'linear-gradient(135deg, #f0f0f0 0%, #d9d9d9 20%, #bfbfbf 40%, #e6e6e6 60%, #c0c0c0 80%, #f5f5f5 100%)',
    color: '#1a1a1a',
    boxShadow:
      '0 2px 6px rgba(160, 160, 160, 0.6), inset 0 1px 2px rgba(255, 255, 255, 0.7), inset 0 -1px 2px rgba(120, 120, 120, 0.2)',
    textShadow: '0 1px 1px rgba(255, 255, 255, 0.8)',
  },
  ouro: {
    background:
      'linear-gradient(135deg, #fff6d0 0%, #ffec99 20%, #ffd84d 40%, #f5c842 60%, #eec44d 80%, #fff2b8 100%)',
    color: '#3a2d00',
    boxShadow:
      '0 2px 6px rgba(230, 180, 60, 0.5), inset 0 1px 2px rgba(255, 245, 200, 0.7), inset 0 -1px 2px rgba(190, 150, 40, 0.25)',
    textShadow: '0 1px 1px rgba(255, 250, 210, 0.85)',
  },
  diamante: {
    background:
      'linear-gradient(135deg, #e0f7ff 0%, #b8eaff 20%, #a0e0f0 40%, #c8f4ff 60%, #e6fbff 80%, #f4fdff 100%)',
    color: '#004040',
    boxShadow:
      '0 2px 8px rgba(140, 220, 240, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(120, 180, 200, 0.3)',
    textShadow: '0 1px 1px rgba(255, 255, 255, 0.85)',
  },
};

const translateType = (type?: string) => {
  switch (type?.toLowerCase()) {
    case 'one-image':
      return 'Uma imagem';
    case 'two-images':
      return 'Duas imagens';
    case 'three-images':
      return 'Três imagens';
    default:
      return 'Uma imagem';
  }
};

export const getHomeBannersColumns = ({
  handleChangeStatus,
}: GetHomeBannersColumnsParams): GridColDef[] => {
  const columns: GridColDef[] = [
    {
      field: 'preview',
      headerName: 'Imagem',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => <BannerPreviewCell banner={params.row} />,
    },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1.2,
      minWidth: 220,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'priority',
      headerName: 'Prioridade',
      type: 'number',
      width: 110,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'type',
      headerName: 'Tipo',
      flex: 1,
      minWidth: 150,
      align: 'left',
      sortable: false,
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const translatedType = translateType(String(params.value || ''));
        return <Box component="span">{translatedType}</Box>;
      },
    },
    {
      field: 'tier',
      headerName: 'Tier',
      headerAlign: 'left',
      align: 'left',
      width: 170,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const rowTiers = Array.isArray(params.row.visibleForTiers)
          ? params.row.visibleForTiers
          : [];
        let normalizedTierValues = [String(params.row.tier || 'all')];

        if (rowTiers.length > 0) {
          normalizedTierValues = rowTiers.includes('all') ? ['all'] : rowTiers;
        }

        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, width: 1 }}>
            {normalizedTierValues.map((tierValue: string) => {
              const translatedTier = translateTier(tierValue);
              const capitalizedTier =
                translatedTier.charAt(0).toUpperCase() + translatedTier.slice(1).toLowerCase();

              return (
                <Tooltip key={`${params.id}-${tierValue}`} title={capitalizedTier}>
                  <Label
                    variant="soft"
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      ...tierStyles[translatedTier.toLowerCase()],
                    }}
                  >
                    {translatedTier}
                  </Label>
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Criado em',
      minWidth: 150,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const formatDate = (isoDate: string) => {
          const date = new Date(isoDate);
          return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        };

        return renderDateCell(params.value, formatDate);
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Atualizado em',
      minWidth: 150,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const formatDate = (isoDate: string) => {
          const date = new Date(isoDate);
          return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        };

        return renderDateCell(params.value, formatDate);
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 100,
      flex: 0.7,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const rawStatus = String(params.value || '')
          .toLowerCase()
          .trim();

        if (rawStatus === 'expired') {
          return (
            <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Label variant="soft" color="error">
                Expirado
              </Label>
            </Box>
          );
        }

        if (rawStatus === 'soldout' || rawStatus === 'sold_out' || rawStatus === 'sold out') {
          return (
            <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Label variant="soft" color="warning">
                Esgotado
              </Label>
            </Box>
          );
        }

        if (rawStatus === 'active' || rawStatus === 'inactive') {
          return (
            <Switch
              checked={rawStatus === 'active'}
              onClick={(e) => e.stopPropagation()}
              onChange={(event) => {
                event.stopPropagation();
                const nextStatus = event.target.checked ? 'active' : 'inactive';
                const benefitId = String(params.row.benefitId || params.id || '');

                handleChangeStatus?.(benefitId, nextStatus);
              }}
              sx={{
                width: 65,
                height: 25,
                p: 0,
                overflow: 'visible',

                '& .MuiSwitch-switchBase': {
                  p: '4px',
                  transitionDuration: '220ms',

                  '&.Mui-checked': {
                    transform: 'translateX(35px)',
                    color: 'common.white',

                    '& + .MuiSwitch-track': {
                      borderColor: 'success.main',
                      background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      boxShadow: (theme) =>
                        `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.18)}, 0 8px 18px ${alpha(theme.palette.success.main, 0.24)}`,
                    },

                    '& + .MuiSwitch-track::before': {
                      opacity: 1,
                    },

                    '& + .MuiSwitch-track::after': {
                      opacity: 0,
                    },
                  },

                  '&.Mui-focusVisible .MuiSwitch-thumb': {
                    outline: (theme) => `3px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                    outlineOffset: 2,
                  },
                },

                '& .MuiSwitch-thumb': {
                  width: 17,
                  height: 17,
                  bgcolor: 'common.white',
                  boxShadow: (theme) =>
                    `0 3px 10px ${alpha(theme.palette.grey[900], 0.22)}, inset 0 0 0 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                },

                '& .MuiSwitch-track': {
                  position: 'relative',
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: 'error.main',
                  opacity: 1,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.darker} 100%)`,
                  boxShadow: (theme) =>
                    `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.16)}, 0 8px 18px ${alpha(theme.palette.error.main, 0.2)}`,

                  '&::before': {
                    content: '"Ativo"',
                    position: 'absolute',
                    left: 9,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#fff',
                    fontSize: 9,
                    lineHeight: 1,
                    fontWeight: 700,
                    letterSpacing: 0,
                    opacity: 0,
                    transition: 'opacity 160ms ease',
                  },

                  '&::after': {
                    content: '"Inativo"',
                    position: 'absolute',
                    right: 7,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#fff',
                    fontSize: 9,
                    lineHeight: 1,
                    fontWeight: 700,
                    letterSpacing: 0,
                    opacity: 1,
                    transition: 'opacity 160ms ease',
                  },
                },
              }}
            />
          );
        }

        return String(params.value || '');
      },
    },
  ];

  return columns.map((column) => ({
    ...column,
    disableColumnMenu: true,
  }));
};
